const stoneFAQ = {
	general: [
		{
			title: "What is Stone?",
			content: `
        <p>Stone is a Windows (10, 11) desktop application used to capture and share knowledge. It facilitates the creation of a veritable research record and engenders trust through its various layers of provenance. By adopting Stone, journalists make a commitment to greater transparency, which elevates well-researched, high-quality reporting.</p>
      `,
		},
		{
			title: "Who uses Stone?",
			content: `
        <ul>
          <li>Journalists</li>
          <li>Academics (Pedagogy)</li>
          <li>Computer Programmers</li>
          <li>Open Source Investigators</li>
          <li>This is not an exhaustive list. As we develop Stone, it will be applicable in a broader range of fields.</li>
        </ul>
      `,
		},
		{
			title: "On what operating system can I install Stone?",
			content: `
        <p>At this time, Stone works exclusively on Windows (10, 11). The windows automatic updates should ensure you have the correct version. Technical details: Minimum version 2004 (10.0.19041.0) with VC++ Redist 2019. Stone will be installable on other operating systems in 2022.</p>
      `,
		},
		{
			title: "How do I download and install the application?",
			content: `
        <p>Simply download and install the application.</p>
        <p>Stone creates video bibliographies. Capture evidence with our free desktop software. Share your research process with the world.</p>
        <p>Visit our website on your desktop browser to download our software: <a href="https://www.writeinstone.com/">https://www.writeinstone.com/</a>.</p>
      `,
		},
		{
			title: "Is Stone available for mobile users?",
			content: `
        <p>Glad to see you're checking out Stone on Mobile. Stone is exclusive to Windows (10, 11) Desktop systems.</p>
        <p>Visit our website on your desktop browser to download our software.</p>
      `,
		},
		{
			title: "How do I create a Stone account?",
			content: `
        <p>When activating the software for the first time, select 'create new account' and input an email address and password. Should you choose to, verify the account using a link sent to the corresponding email address.</p>
      `,
		},
		{
			title: "How do I handle 'wrong version' errors when installing Stone?",
			content: `
        <p>If you receive a Windows message stating you have the 'wrong version', ensure that your version of Windows is fully updated.</p>
      `,
		},
		{
			title: "What should I do if I get the message 'Login failed'?",
			content: `
        <p>Check that your system clock is accurate. Ideally, have it automatically sync to a time provider on the web. If your computer's clock is not exactly right, it will not accept the server's authorization token.</p>
      `,
		},
		{
			title: "Is captured research secure on the Stone server?",
			content: `
        <p>Stone is in Beta, therefore it should only be used when conducting research that can safely be shared publicly. Research will not be sharable until published, but we cannot guarantee that third parties will not be able to access material stored on our servers. Stone's servers are based in Germany, making us subject to GDPR compliance.</p>
      `,
		},
		{
			title: "Will Stone share personal data with third parties?",
			content: `
        <p>No. We collect data on Stone software users and those who interact with produced content. This data is captured and stored in a manner that protects privacy (IPs are hashed). In addition to pseudonymous data, Stone retains a list of email addresses used to log in to our system. Our company maintains strict protocols for sharing this information in-house and does not share email addresses with third parties.</p>
      `,
		},
		{
			title:
				"Once Stone is installed, does it capture information when not activated?",
			content: `
        <p>No. The screen capture function is activated only when a user selects to do so.</p>
      `,
		},
		{
			title: "Can a Stone account be deleted?",
			content: `
        <p>Yes. Note that this cannot be done without the assistance of a member of the WriteInStone team. Please contact us for further information using the Contact Us feature in the desktop client or by emailing <a href="mailto:info@writeinstone.com">info@writeinstone.com</a>.</p>
      `,
		},
		{
			title: "What happens when I first launch the application?",
			content: `
        <p>On first launch, you will be prompted to create an account using your email address. Stone will also ask you to create a video bio, a short clip that goes at the end of all published Projects so your audience can get to know and support you.</p>
      `,
		},
		{
			title: "How do I capture my work?",
			content: `
        <p>Capture at the desk using screen capture or by uploading external media recorded in the field.</p>
      `,
		},
		{
			title: "How can I highlight key moments?",
			content: `
        <p>Use your webcam while you work, or select important sections of external media.</p>
      `,
		},
		{
			title: "How do I review my work?",
			content: `
        <p>Stone is a "Linear Video Editor," meaning most of the work is done as you go. If you have used external media, you can add highlights to that. Stone enables you to permanently remove any sensitive personal information using the redact feature. Review your research before publishing to ensure it's ready for the world.</p>
      `,
		},
		{
			title: "How do I share my work with the world?",
			content: `
        <p>Once you've finished and clicked "Publish," you will be able to:</p>
        <ul>
          <li>Share a public-facing Project Page with a complete video timeline of your research.</li>
          <li>Embed a Research Portal in your article, which plays highlights and links to the Project Page.</li>
          <li>Copy a QR code for paper documents that links to your Project Page.</li>
        </ul>
      `,
		},
	],
	creatingContent: [
		{
			title: "Must I create a video bio?",
			content:
				"When using Stone for the first time, a user will be prompted to create a video bio. A user can update their video bio from the profile window on the desktop client.",
		},
		{
			title: "What are projects and to what do they correspond?",
			content:
				"A project most often relates to a specific line of research and comprises all research events. For instance, a journalist using Stone would likely create a single project to capture research pertaining to a specific story.",
		},
		{
			title:
				"What are desktop research sessions and how do they differ from projects?",
			content:
				"A desktop session is a type of research event that utilizes screen capture and webcam commentary to produce a record of work done (on the computer desktop) and video annotations, which are called 'highlights'.",
		},
		{
			title:
				"What are highlights and how are they added during a desktop session?",
			content:
				"Highlights are webcam commentaries which users can add at key points in the research process. Highlights are added by clicking on the camera icon on the recording window during a desktop research session.",
		},
		{
			title: "What is the retake function and how does it work?",
			content:
				"When conducting a highlight, the retake function enables a user to delete the ongoing highlight and begin another. This function is activated by selecting the dumbslate (clapperboard). The highlights which are 'retaken' will not appear in the embedded highlight reel, whereas the highlight produced after the last retake will appear on the reel.",
		},
		{
			title: "During a highlight, how many times can I 'retake'?",
			content:
				"There is no set limit on the number of times a user can select 'retake', though it is recommended that a user limit their use of this function to ensure sound performance.",
		},
		{
			title: "What does the trash function do and how does it work?",
			content:
				"When conducting a highlight, the trash function enables a user to delete the ongoing highlight and return to work mode. This function is activated by selecting the trash icon. The 'trashed' (deleted) highlight will not appear in the embedded highlight reel.",
		},
		{
			title: "What does the crop function do and how does it work?",
			content:
				"When selecting to highlight, the user has two choices: standard highlight or cropped highlight. A cropped highlight enables the user to focus on a selected section of the screen and move the webcam image inside the selected area. All other functions during a cropped highlight are the same as in a standard highlight.",
		},
		{
			title: "Can the crop be reset once a portion of the screen is selected?",
			content:
				"Yes, after selecting a portion of the screen to crop, the user can press and hold the left-mouse button again to reset.",
		},
		{
			title: "What is external media and how is it added to a project?",
			content:
				"External media comprise Mp3 and Mp4 files captured on external devices such as a digital camera or phone. These can be uploaded via the desktop client and will appear alongside other research events in a project timeline.",
		},
		{
			title: "How are highlights added to external media?",
			content:
				"Once external media has been uploaded via the desktop client, select the event chip (thumbnail) corresponding to the external media to be highlighted. Then select 'add highlight'.",
		},
		{
			title: "Can I create a research session without placing it in a project?",
			content:
				"As currently constituted, Stone does not support this functionality.",
		},
		{
			title: "How do I delete a session?",
			content:
				"Select the session for deletion from the timeline (specific event chip [thumbnail]). Select the trash icon from within the event chip and confirm the selection.",
		},
		{
			title:
				"A desktop session was ended erroneously. Is there a way to return to the session without having to start a new one?",
			content: "Once the naming prompt window appears, select 'back'.",
		},
		{
			title: "How do I delete a project?",
			content:
				"Select the project for deletion from the project list, which is accessible via the 'projects' drop down menu in the top left corner of the desktop client. Then select the corresponding trash icon on the right-hand side of the project list.",
		},
		{
			title: "How do I end a research session and start over?",
			content:
				"Currently, there is no option to 'redo' a session. Instead, a user must select the event chip (thumbnail) corresponding to the desktop session, delete it (as described above), and begin a new session.",
		},
		{
			title: "Will the webcam be activated every time I create a highlight?",
			content: "The webcam will activate each time a highlight is initiated.",
		},
		{
			title: "How do I redact portions of desktop research?",
			content:
				"Select the event chip corresponding to the desktop session from which a portion of research is to be redacted. Select 'redact' and adjust the section highlighted in red. Confirm the deletion. NOTE: Redactions are permanent. Once sections of an event are deleted, they cannot be recovered.",
		},
		{
			title: "Can I redact portions of an external media upload?",
			content:
				"Yes. Use the same procedure as described for redacting portions of desktop research sessions.",
		},
	],
	sharingContent: [
		{
			title: "What does 'publish' do?",
			content:
				"Publishing a project makes the materials contained within the project (both desktop research and external media) publicly accessible. Once published, the share menu will be accessible.",
		},
		{
			title: "Can stories be 'unpublished'?",
			content:
				"Yes. When on the editing page of the project to be unpublished, select 'unpublish' on the Projects dropdown menu.",
		},
		{
			title: "What is an embedded highlight reel?",
			content:
				"Using whichever method is appropriate or preferred (URL or HTML embed), a primary user can share research highlights in an embedded player on CMS platforms. Select the share button and copy either the URL or HTML code and paste into the appropriate position.",
		},
		{
			title: "What is the public research page?",
			content:
				"The public research page is a webpage on which media consumers can review and scrutinize the full research record.",
		},
		{
			title:
				"What will play on a highlight reel if no highlights were made in any session of a project?",
			content:
				"When no highlights have been produced in a published project, the embedded highlight reel will exclusively display the author's video bio.",
		},
		{
			title:
				"Can highlights be added to a desktop research session after it has been completed?",
			content:
				"No. Desktop sessions can only be edited using the redact feature.",
		},
		{
			title: "How can readers access the full research record?",
			content:
				"Either by clicking on the link at the bottom of the embedded player or by clicking on a provided URL which links to the public page.",
		},
		{
			title:
				"Once a project is updated, will added highlights play in the corresponding highlight reel and public research page?",
			content:
				"Yes. Unpublished additions to already published projects will not display on the public research page and unpublished highlights will not play in the embedded highlight reel.",
		},
		{
			title:
				"Once a project is updated, will deleted content be removed from the corresponding highlight reel and public research page?",
			content: "Yes.",
		},
		{
			title:
				"If there is no playback on the highlight reel and/or the public research page, what steps should I take to ensure playback?",
			content: [
				"Check that the project has been both published and updated.",
				"Reload the page on which the highlight reel is embedded or reload the public research page.",
				"If one is unable to initiate playback, use the Contact Us feature or email our team at info@writeinstone.com to offer feedback and request assistance.",
			],
		},
	],
};

export default stoneFAQ;
